// Task #2682 — Picker modelli a due passi (provider → modello) per il Site
// Builder. Il dropdown provider è alimentato da `/ai-tools/available-providers`
// (filtrato per capability), il modello dal catalog live via `ModelPicker`.
// Il provider selezionato nel dropdown è la fonte di verità e viene emesso via
// `onChange(modelId, provider)`; la pre-selezione si deriva dal prefisso dell'id.
import { useState, useEffect, useMemo } from 'react';
import {
  ModelPicker,
  type ModelCapability,
  type ModelProviderKey,
} from '@easyflow/ui-frontend';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { apiClient } from '@/lib/api';

export interface AvailableProviderEntry {
  id: string;
  label: string;
  kind: 'canonical' | 'custom';
  capability_supported: boolean;
  is_valid: boolean;
  disabled_reason: string | null;
  reason_code: string | null;
  health_status: string | null;
}

interface AvailableProvidersData {
  service_key: string;
  capability: string;
  providers: AvailableProviderEntry[];
}

// Provider canonici instradabili dalla generazione (ModelProviderKey). Le righe
// `custom:<uuid>` non sono selezionabili: `detectProvider` lato BE ricava solo
// openai/claude/gemini dal prefisso dell'id.
const CANONICAL_PROVIDER_KEYS: readonly ModelProviderKey[] = [
  'openai',
  'claude',
  'google',
];

function isModelProviderKey(id: string): id is ModelProviderKey {
  return (CANONICAL_PROVIDER_KEYS as readonly string[]).includes(id);
}

// Pre-selezione provider derivata dal prefisso dell'id (stessa logica di
// `detectProvider` lato backend): gemini-* → google, claude-* → claude, else openai.
function deriveProviderKey(modelId: string): ModelProviderKey {
  if (modelId.startsWith('claude-')) return 'claude';
  if (modelId.startsWith('gemini-')) return 'google';
  return 'openai';
}

export interface TwoStepModelPickerProps {
  label: string;
  helpText?: string;
  capability: ModelCapability;
  serviceKey: string;
  value: string;
  /**
   * Emesso alla scelta di un modello. `provider` è il provider canonico
   * selezionato nel dropdown (fonte di verità per il payload `{ provider, model }`
   * di PUT /ai-tools/service-models), NON derivato dal prefisso dell'id.
   */
  onChange: (modelId: string, provider: ModelProviderKey) => void;
  /** Nasconde i modelli deprecati nella select modello (default: false). */
  hideDeprecated?: boolean;
}

export function TwoStepModelPicker(props: TwoStepModelPickerProps): JSX.Element {
  const { label, helpText, capability, serviceKey, value, onChange, hideDeprecated } = props;
  const [providers, setProviders] = useState<AvailableProviderEntry[]>([]);
  const [provider, setProvider] = useState<ModelProviderKey>(
    value ? deriveProviderKey(value) : 'openai',
  );

  useEffect(() => {
    let cancelled = false;
    const load = async (): Promise<void> => {
      try {
        const res = await apiClient.get<AvailableProvidersData>(
          `/ai-tools/available-providers?service_key=${encodeURIComponent(serviceKey)}`,
        );
        if (!cancelled && res.success && res.data) {
          // Mostra SOLO provider canonici che supportano la capability E che
          // hanno una chiave API valida per il tenant (`is_valid`): un provider
          // senza chiave non è instradabile, non deve comparire nel dropdown.
          setProviders(
            res.data.providers.filter(
              (p) => p.kind === 'canonical' && p.capability_supported && p.is_valid,
            ),
          );
        }
      } catch {
        if (!cancelled) setProviders([]);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [serviceKey]);

  // Hydration: quando una pagina viene aperta con un modello già salvato, il
  // dropdown provider si pre-seleziona derivando il provider dall'id.
  useEffect(() => {
    if (value) setProvider(deriveProviderKey(value));
  }, [value]);

  const selectableProviders = useMemo(
    () => providers.filter((p) => isModelProviderKey(p.id)),
    [providers],
  );

  // Riallinea il provider corrente al primo selezionabile quando quello attivo
  // non è eseguibile (es. default 'openai' su tenant solo-Gemini, o un modello
  // salvato il cui provider non è più valido). Senza questo, `ModelPicker`
  // riceverebbe `provider={provider}` non eseguibile e ne mostrerebbe comunque il
  // catalogo, violando l'invariante "picker == eseguibile".
  useEffect(() => {
    if (selectableProviders.length === 0) return;
    if (selectableProviders.some((p) => p.id === provider)) return;
    const first = selectableProviders[0];
    if (first && isModelProviderKey(first.id)) setProvider(first.id);
  }, [selectableProviders, provider]);

  // Nessun provider eseguibile selezionato → il catalog modello va disabilitato,
  // così non si può scegliere un modello di un provider non instradabile.
  const hasSelectableProvider = selectableProviders.some((p) => p.id === provider);

  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold">{label}</Label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Select
          value={provider}
          onValueChange={(v) => {
            if (isModelProviderKey(v)) setProvider(v);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Provider…" />
          </SelectTrigger>
          <SelectContent>
            {selectableProviders.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <ModelPicker
          capability={capability}
          provider={provider}
          serviceKey={serviceKey}
          value={value}
          onChange={(modelId) => onChange(modelId, provider)}
          hideDeprecated={hideDeprecated}
          disabled={!hasSelectableProvider}
        />
      </div>
      {helpText && <p className="text-xs text-muted-foreground">{helpText}</p>}
    </div>
  );
}

export default TwoStepModelPicker;
